import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { NavBar } from 'components/NavBar/NavBar'
import { Tabs } from 'components/Tabs/Tabs'
import { TabProvider, useTabContext } from 'components/Tabs/TabContext'
import {
  Button,
  ColorVariant,
  Flex,
  MainHeadline,
  PageWrapper,
} from 'styling/GlobalStyles'
import { NormalText } from 'styling/TypographyV2'
import { AppDispatch } from 'store'
import { clearOperationalManagement } from 'features/operationalManagement/operationalManagementSlice'
import { ExportIcon } from 'assets/icons/ExportIcon'
import { PeopleGroupIcon } from './icons'
import { OrganogramHeader } from './Organogram.styled'
import { OverviewTab } from './tabs/OverviewTab'
import { SitesTab } from './tabs/SitesTab'
import { TeamsTab } from './tabs/TeamsTab'
import { OrgStructureTab } from './tabs/OrgStructureTab'
import { AssignmentsTab } from './tabs/AssignmentsTab'
import { ComingSoonTab } from './tabs/ComingSoonTab'
import { CreateSiteModal } from './components/CreateSiteModal'
import { CreateTeamModal } from './components/CreateTeamModal'
import { CreateAssignmentModal } from './components/CreateAssignmentModal'
import { BulkAssignWizard } from './BulkAssign/BulkAssignWizard'

const TAB_NAMES = [
  'Overview',
  'Org Structure',
  'Teams',
  'Sites',
  'Assignments',
  'Audit Log',
] as const
type TabName = typeof TAB_NAMES[number]
const TabBody = ({
  sitesRefreshKey,
  teamsRefreshKey,
  assignmentsRefreshKey,
  onSiteUpdated,
}: {
  sitesRefreshKey: number
  teamsRefreshKey: number
  assignmentsRefreshKey: number
  onSiteUpdated: () => void
}) => {
  const { activeTab } = useTabContext()
  switch (activeTab) {
    case 'Overview':
      return <OverviewTab />
    case 'Org Structure':
      return <OrgStructureTab />
    case 'Teams':
      return <TeamsTab refreshKey={teamsRefreshKey} />
    case 'Sites':
      return <SitesTab refreshKey={sitesRefreshKey} onSiteUpdated={onSiteUpdated} />
    case 'Assignments':
      return <AssignmentsTab refreshKey={assignmentsRefreshKey} />
    case null:
    case undefined:
      return null
    default:
      return <ComingSoonTab name={activeTab} />
  }
}

const HeaderContent = ({
  onCreateSite,
  onCreateTeam,
  onCreateAssignment,
  onBulkAssign,
}: {
  onCreateSite: () => void
  onCreateTeam: () => void
  onCreateAssignment: () => void
  onBulkAssign: () => void
}) => {
  const { activeTab } = useTabContext()
  const isAssignments = activeTab === 'Assignments'

  const subtitle = isAssignments
    ? 'Global view of all operational assignments'
    : 'Operational structure overview'

  return (
    <OrganogramHeader>
      <Flex column gap='0.25rem'>
        <MainHeadline>Organogram</MainHeadline>
        <NormalText semibold colorVariant={ColorVariant.BASE}>
          {subtitle}
        </NormalText>
      </Flex>
      <Flex gap='0.75rem' wrap>
        {isAssignments ? (
          <>
            <Button orangeBorder width='140'>
              <Flex center gap='0.5rem'>
                <span style={{ display: 'inline-flex' }}>{ExportIcon}</span>
                Export list
              </Flex>
            </Button>
            <Button orangeBorder width='140' onClick={onBulkAssign}>
              <Flex center gap='0.5rem'>
                <span style={{ display: 'inline-flex' }}>
                  <PeopleGroupIcon size={16} />
                </span>
                Bulk Assign
              </Flex>
            </Button>
            <Button orange width='180' onClick={onCreateAssignment}>
              + Create assignment
            </Button>
          </>
        ) : (
          <>
            <Button orange onClick={onCreateTeam}>
              + Create Team
            </Button>
            <Button orange onClick={onCreateSite}>
              + Create Site
            </Button>
          </>
        )}
      </Flex>
    </OrganogramHeader>
  )
}

export const Organogram = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [createSiteOpen, setCreateSiteOpen] = useState(false)
  const [createTeamOpen, setCreateTeamOpen] = useState(false)
  const [createAssignmentOpen, setCreateAssignmentOpen] = useState(false)
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false)
  const [sitesRefreshKey, setSitesRefreshKey] = useState(0)
  const [teamsRefreshKey, setTeamsRefreshKey] = useState(0)
  const [assignmentsRefreshKey, setAssignmentsRefreshKey] = useState(0)

  useEffect(() => {
    return () => {
      dispatch(clearOperationalManagement())
    }
  }, [dispatch])

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <TabProvider tabs={[...TAB_NAMES]} groupName='tab'>
          {bulkAssignOpen ? (
            <BulkAssignWizard
              onClose={() => {
                setBulkAssignOpen(false)
                setAssignmentsRefreshKey((k) => k + 1)
              }}
            />
          ) : (
            <>
              <HeaderContent
                onCreateSite={() => setCreateSiteOpen(true)}
                onCreateTeam={() => setCreateTeamOpen(true)}
                onCreateAssignment={() => setCreateAssignmentOpen(true)}
                onBulkAssign={() => setBulkAssignOpen(true)}
              />

              <Flex>
                <Tabs />
              </Flex>
              <div style={{ marginTop: '1.25rem' }}>
                <TabBody
                  sitesRefreshKey={sitesRefreshKey}
                  teamsRefreshKey={teamsRefreshKey}
                  assignmentsRefreshKey={assignmentsRefreshKey}
                  onSiteUpdated={() => setSitesRefreshKey((k) => k + 1)}
                />
              </div>
            </>
          )}
        </TabProvider>

        {createSiteOpen && (
          <CreateSiteModal
            onClose={() => setCreateSiteOpen(false)}
            onCreated={() => setSitesRefreshKey((k) => k + 1)}
          />
        )}

        {createTeamOpen && (
          <CreateTeamModal
            onClose={() => setCreateTeamOpen(false)}
            onCreated={() => setTeamsRefreshKey((k) => k + 1)}
          />
        )}

        {createAssignmentOpen && (
          <CreateAssignmentModal
            onClose={() => setCreateAssignmentOpen(false)}
            onCreated={() => setAssignmentsRefreshKey((k) => k + 1)}
          />
        )}
      </PageWrapper>
    </>
  )
}
